import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { fmtEur, fmtDateTime } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import VersionBadge from "@/components/VersionBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Send, Edit, Check, X, Clock, Copy, Trash2, FileText, Mail, History,
  Link2, Upload, RefreshCw, FileSignature, Paperclip, ExternalLink, Eye, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import PdfPreviewModal from "@/components/PdfPreviewModal";

export default function OfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [publicUrl, setPublicUrl] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [contractEmailOpen, setContractEmailOpen] = useState(false);
  const [contractEmailForm, setContractEmailForm] = useState({ to: "", subject: "", message: "", cc_interno: true });
  const [sendingContract, setSendingContract] = useState(false);
  const [rejectMsg, setRejectMsg] = useState("");
  const [emailData, setEmailData] = useState({ to: "", subject: "", body: "", cc_interno: false });
  const [settings, setSettings] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: s }, { data: atts }, { data: vers }] = await Promise.all([
        api.get(`/offers/${id}`),
        api.get("/settings"),
        api.get(`/offers/${id}/attachments`).catch(() => ({ data: [] })),
        api.get(`/offers/${id}/versions`).catch(() => ({ data: [] })),
      ]);
      setO(data);
      setSettings(s);
      setAttachments(atts || []);
      setVersions(vers || []);
      if (data.public_token) {
        const url = `${window.location.origin}/p/offerta/${data.public_token}`;
        setPublicUrl(url);
      } else {
        setPublicUrl(null);
      }
      setEmailData((e) => ({
        ...e,
        to: data.cliente?.email || "",
        subject: s.email_template_subject || "",
        body: s.email_template_body || "",
      }));
    } catch (err) {
      toast.error("Offerta non trovata");
      navigate("/offerte");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !o) return <div className="text-slate-400 text-center py-16">Caricamento…</div>;

  const canApprove = ["admin", "direzione"].includes(user?.role);
  const canSend = ["admin", "direzione", "commerciale"].includes(user?.role);
  const canPublicLink = ["admin", "direzione", "commerciale"].includes(user?.role);

  const submit = async () => { try { await api.post(`/offers/${id}/submit`); toast.success("Inviata in approvazione"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const approve = async () => { try { await api.post(`/offers/${id}/approve`, {}); toast.success("Approvata"); load(); } catch (e) { toast.error("Errore"); } };
  const reject = async () => { try { await api.post(`/offers/${id}/reject`, { commento: rejectMsg }); toast.success("Rimandata al mittente"); setRejectOpen(false); load(); } catch (e) { toast.error("Errore"); } };
  const mark = async (stato) => { try { await api.post(`/offers/${id}/mark`, { stato }); toast.success(`Segnata come ${stato}`); load(); } catch (e) { toast.error("Errore"); } };
  const send = async () => {
    try {
      const { data } = await api.post(`/offers/${id}/send`, emailData);
      if (data.result?.status === "sent") toast.success("Email inviata");
      else if (data.result?.status === "mock") toast.warning("Email in modalità MOCK (RESEND_API_KEY mancante)");
      else toast.error(`Invio fallito: ${data.result?.error || data.result?.status}`);
      setSendOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    }
  };
  const sendContract = async () => {
    setSendingContract(true);
    try {
      const { data } = await api.post(`/offers/${id}/contract/send`, contractEmailForm);
      if (data.status === "sent") toast.success(`Contratto inviato a ${data.to}`);
      else if (data.status === "mock") toast.warning("Contratto pronto (modalità MOCK — RESEND_API_KEY mancante)");
      else toast.error(`Invio fallito: ${data.result?.error || data.status}`);
      setContractEmailOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setSendingContract(false);
    }
  };
  const duplicate = async () => { try { const { data } = await api.post(`/offers/${id}/duplicate`); toast.success(`Duplicata come ${data.numero}`); navigate(`/offerte/${data.id}`); } catch (e) { toast.error("Errore"); } };
  const renew = async () => { try { const { data } = await api.post(`/offers/${id}/renew`); toast.success(`Rinnovo creato: ${data.numero}`); navigate(`/offerte/${data.id}`); } catch (e) { toast.error("Errore"); } };
  const del = async () => { if (!confirm("Eliminare definitivamente?")) return; try { await api.delete(`/offers/${id}`); toast.success("Eliminata"); navigate("/offerte"); } catch (e) { toast.error("Errore"); } };

  const generatePublicLink = async () => {
    try {
      const { data } = await api.post(`/offers/${id}/public-link`);
      setPublicUrl(data.url);
      setLinkOpen(true);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copiato"); } catch { toast.error("Impossibile copiare"); } };

  const uploadFile = async (file, tipo = "documento") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tipo", tipo);
    try {
      await api.post(`/offers/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore upload"); }
  };
  const delAttachment = async (attId) => {
    if (!confirm("Eliminare l'allegato?")) return;
    try { await api.delete(`/attachments/${attId}`); toast.success("Eliminato"); load(); } catch (e) { toast.error("Errore"); }
  };

  const openPdf = () => {
    const url = `${API_BASE}/offers/${id}/pdf`;
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;
  };
  const previewPdf = () => {
    const url = `${API_BASE}/offers/${id}/pdf?inline=true`;
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;
  };

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate("/offerte")} className="p-2 rounded hover:bg-slate-100 mt-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Offerta</div>
            <div className="flex items-center gap-3 mt-0.5">
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F2C59]" data-testid="offer-numero">{o.numero}</h1>
              <VersionBadge version={o.versione} alwaysShow />
              <StatusBadge stato={o.stato} />
            </div>
            <div className="text-slate-500 mt-1">{o.cliente?.ragione_sociale}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={previewPdf} data-testid="preview-pdf-btn">
            <Eye className="w-4 h-4 mr-1" /> Anteprima PDF
          </Button>
          <Button variant="outline" onClick={openPdf} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> Scarica PDF
          </Button>
          <Button variant="outline" onClick={duplicate} data-testid="duplicate-btn">
            <Copy className="w-4 h-4 mr-1" /> Duplica
          </Button>
          {!["da_approvare", "rifiutata"].includes(o.stato) && (
            <Button
              variant="outline"
              onClick={() => {
                if (["approvata", "inviata", "accettata"].includes(o.stato)) {
                  if (!window.confirm(`Modificare un'offerta in stato "${o.stato}" creerà una nuova versione (v${(o.versione || 1) + 1}) e potrebbe richiedere una nuova approvazione. Procedere?`)) return;
                }
                navigate(`/offerte/${id}/modifica`);
              }}
              className="border-[#0F2C59] text-[#0F2C59] hover:bg-blue-50"
              data-testid="edit-btn"
            >
              <Edit className="w-4 h-4 mr-1" /> Modifica
            </Button>
          )}
          {["bozza", "da_correggere"].includes(o.stato) && (
            <Button onClick={submit} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="submit-btn">
              <Send className="w-4 h-4 mr-1" /> Invia in approvazione
            </Button>
          )}
          {o.stato === "da_approvare" && canApprove && (
            <>
              <Button onClick={approve} className="bg-green-600 hover:bg-green-700" data-testid="approve-btn">
                <Check className="w-4 h-4 mr-1" /> Approva
              </Button>
              <Button variant="outline" onClick={() => setRejectOpen(true)} className="text-red-600" data-testid="reject-btn">
                <X className="w-4 h-4 mr-1" /> Rimanda al mittente
              </Button>
            </>
          )}
          {o.stato === "approvata" && canSend && (
            <Button onClick={() => setSendOpen(true)} className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0F2C59] font-semibold" data-testid="send-email-btn">
              <Mail className="w-4 h-4 mr-1" /> Invia al cliente
            </Button>
          )}
          {["approvata", "inviata"].includes(o.stato) && canPublicLink && (
            <Button variant="outline" onClick={generatePublicLink} data-testid="public-link-btn">
              <Link2 className="w-4 h-4 mr-1" /> {o.public_token ? "Mostra link pubblico" : "Genera link pubblico"}
            </Button>
          )}
          {o.stato === "accettata" && (
            <>
              <Button variant="outline" onClick={renew} className="border-[#FACC15] text-[#0F2C59]" data-testid="renew-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Rinnovo annuale
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const url = `${API_BASE}/offers/${id}/certificate`;
                  const w = window.open(url, "_blank");
                  if (!w) window.location.href = url;
                }}
                className="border-green-600 text-green-700 hover:bg-green-50"
                data-testid="download-certificate-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-1" /> Certificato firmato
              </Button>
            </>
          )}
          {["approvata", "inviata", "accettata"].includes(o.stato) && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  const url = `${API_BASE}/offers/${id}/contract?format=pdf`;
                  const w = window.open(url, "_blank");
                  if (!w) window.location.href = url;
                }}
                className="border-[#0F2C59] bg-[#FEF9C3] text-[#0F2C59] hover:bg-[#FACC15]/40"
                title="Genera e scarica il contratto finale (PDF) basato sui dati di questa offerta"
                data-testid="download-contract-btn"
              >
                <FileSignature className="w-4 h-4 mr-1" /> Contratto finale
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setContractEmailForm({
                    to: o.cliente?.email || "",
                    subject: `Contratto ${o.numero?.replace("OFF-", "CTR-")} · Centro Sicurezza Consulenze`,
                    message: "",
                    cc_interno: true,
                  });
                  setContractEmailOpen(true);
                }}
                className="border-[#0F2C59] text-[#0F2C59] hover:bg-blue-50"
                title="Invia il contratto al cliente via e-mail (con PDF in allegato)"
                data-testid="send-contract-email-btn"
              >
                <Mail className="w-4 h-4 mr-1" /> Invia contratto
              </Button>
            </>
          )}
          {o.stato === "inviata" && (
            <>
              <Button onClick={() => mark("accettata")} className="bg-green-600 hover:bg-green-700" data-testid="mark-accepted-btn">
                <Check className="w-4 h-4 mr-1" /> Accettata
              </Button>
              <Button variant="outline" onClick={() => mark("rifiutata")} className="text-red-600" data-testid="mark-rejected-btn">
                <X className="w-4 h-4 mr-1" /> Rifiutata
              </Button>
              <Button variant="outline" onClick={() => mark("scaduta")}>
                <Clock className="w-4 h-4 mr-1" /> Scaduta
              </Button>
            </>
          )}
          {canApprove && (
            <Button variant="outline" onClick={del} className="text-red-600" data-testid="delete-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="font-display font-semibold text-[#0F2C59] mb-4">Voci offerta</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b">
                  <tr><th className="text-left py-2">Servizio</th><th className="text-right py-2">Q.tà</th><th className="text-right py-2">Prezzo</th><th className="text-right py-2">Totale</th></tr>
                </thead>
                <tbody>
                  {o.lines?.map((l) => (
                    <tr key={l.id} className="border-b border-slate-100">
                      <td className="py-2.5">
                        <div className="font-medium">{l.nome}</div>
                        {l.descrizione && <div className="text-xs text-slate-500">{l.descrizione}</div>}
                      </td>
                      <td className="text-right font-mono py-2.5">{l.quantita} {l.unita}</td>
                      <td className="text-right font-mono py-2.5">{fmtEur(l.prezzo_unitario)}</td>
                      <td className="text-right font-mono py-2.5">{fmtEur(l.totale)}</td>
                    </tr>
                  ))}
                  {o.accessori?.filter((a) => a.totale > 0).map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 text-slate-600">
                      <td className="py-2 italic text-sm">{a.descrizione}</td>
                      <td className="text-right font-mono py-2">{a.quantita}</td>
                      <td className="text-right font-mono py-2">{fmtEur(a.prezzo_unitario)}</td>
                      <td className="text-right font-mono py-2">{fmtEur(a.totale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {(o.note_visibili || o.esclusioni || o.condizioni) && (
            <Card className="p-6 space-y-3">
              <div className="font-display font-semibold text-[#0F2C59]">Note e condizioni</div>
              {o.note_visibili && <div><div className="text-xs uppercase text-slate-500 font-mono">Note</div><div className="text-sm mt-1">{o.note_visibili}</div></div>}
              {o.esclusioni && <div><div className="text-xs uppercase text-slate-500 font-mono">Esclusioni</div><div className="text-sm mt-1">{o.esclusioni}</div></div>}
              {o.note_interne && <div className="p-3 bg-orange-50 border border-orange-200 rounded"><div className="text-xs uppercase text-orange-700 font-mono">Note interne (non visibili al cliente)</div><div className="text-sm mt-1">{o.note_interne}</div></div>}
            </Card>
          )}

          <Card className="p-6">
            <div className="font-display font-semibold text-[#0F2C59] mb-4 flex items-center gap-2"><History className="w-4 h-4" /> Storico</div>
            <div className="space-y-2">
              {(o.approval_log || []).length === 0 && <div className="text-sm text-slate-400">Nessuna attività</div>}
              {(o.approval_log || []).map((l) => (
                <div key={l.id} className="text-sm flex gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="text-xs font-mono text-slate-500 whitespace-nowrap">{fmtDateTime(l.timestamp)}</div>
                  <div className="flex-1">
                    <span className="font-medium text-[#0F2C59]">{l.user_name}</span>
                    {" — "}
                    <span className="text-slate-600">{l.action}</span>
                    {l.commento && <span className="text-slate-500"> · {l.commento}</span>}
                  </div>
                </div>
              ))}
            </div>
            {(o.email_log || []).length > 0 && (
              <>
                <div className="text-xs uppercase text-slate-500 font-mono mt-4 mb-2">Email inviate</div>
                {o.email_log.map((e) => (
                  <div key={e.id} className="text-sm flex gap-3 py-1.5">
                    <div className="text-xs font-mono text-slate-500 whitespace-nowrap">{fmtDateTime(e.sent_at)}</div>
                    <div>a <strong>{e.to}</strong> — {e.status}</div>
                  </div>
                ))}
              </>
            )}
          </Card>

          {/* Allegati */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-semibold text-[#0F2C59] flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Allegati ({attachments.length})
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "documento")}
                    data-testid="upload-doc-input"
                  />
                  <span className="inline-flex items-center text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Documento
                  </span>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "pdf_firmato")}
                    data-testid="upload-signed-input"
                  />
                  <span className="inline-flex items-center text-sm px-3 py-1.5 border border-[#FACC15] bg-[#FEF9C3] text-[#0F2C59] rounded-md hover:bg-[#FACC15] cursor-pointer font-medium">
                    <FileSignature className="w-3.5 h-3.5 mr-1" /> PDF firmato
                  </span>
                </label>
              </div>
            </div>
            {attachments.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-6">Nessun allegato</div>
            ) : (
              <div className="space-y-2">
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50" data-testid={`attachment-${a.id}`}>
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center ${a.tipo === "pdf_firmato" ? "bg-[#FEF9C3] text-[#EAB308]" : "bg-slate-100 text-slate-600"}`}>
                      {a.tipo === "pdf_firmato" ? <FileSignature className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.original_name}</div>
                      <div className="text-xs text-slate-500">
                        {a.tipo === "pdf_firmato" ? "PDF firmato" : "Documento"} · {(a.size / 1024).toFixed(0)} KB · {fmtDateTime(a.uploaded_at)} · {a.uploaded_by_name}
                      </div>
                    </div>
                    <a
                      href={`${API_BASE}/attachments/${a.id}/download`}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                      title="Scarica"
                      data-testid={`download-att-${a.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    {canApprove && (
                      <button onClick={() => delAttachment(a.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Elimina">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Versioni storiche */}
          {versions.length > 0 && (
            <Card className="p-6">
              <div className="font-display font-semibold text-[#0F2C59] mb-3 flex items-center gap-2">
                <History className="w-4 h-4" /> Storico versioni ({versions.length})
              </div>
              <div className="space-y-2">
                {versions.map((v) => (
                  <div key={v.id} className="p-3 border border-slate-200 rounded-md text-sm" data-testid={`version-${v.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-medium text-[#0F2C59]">v{v.numero_versione}</span>
                      <span className="text-xs text-slate-500">{fmtDateTime(v.created_at)}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{v.motivo} · {v.created_by_name}</div>
                    <div className="text-xs mt-2 font-mono">Totale: {fmtEur(v.snapshot?.totale || 0)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right: totals + client */}
        <div className="space-y-6">
          <Card className="p-6">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale offerta</div>
            <div className="mt-2 font-display text-4xl font-bold text-[#0F2C59]" data-testid="offer-total">{fmtEur(o.totale)}</div>
            <div className="text-sm text-slate-500 mt-1">≈ {fmtEur(o.valore_mensile)}/mese</div>
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotale</span><span className="font-mono">{fmtEur(o.subtotale)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Sconto</span><span className="font-mono">-{fmtEur(o.sconto_totale)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Imponibile</span><span className="font-mono">{fmtEur(o.imponibile)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">IVA</span><span className="font-mono">{fmtEur(o.iva_totale)}</span></div>
            </div>
          </Card>
          {o.cliente && (
            <Card className="p-6">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">Cliente</div>
              <div className="font-display font-semibold text-[#0F2C59]">{o.cliente.ragione_sociale}</div>
              <div className="text-sm text-slate-500 mt-1 space-y-0.5">
                {o.cliente.partita_iva && <div>P.IVA {o.cliente.partita_iva}</div>}
                {o.cliente.indirizzo && <div>{o.cliente.indirizzo}</div>}
                {o.cliente.comune && <div>{o.cliente.cap} {o.cliente.comune} ({o.cliente.provincia})</div>}
                {o.cliente.email && <div>{o.cliente.email}</div>}
                {o.cliente.telefono && <div>{o.cliente.telefono}</div>}
              </div>
              <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => navigate(`/clienti/${o.cliente.id}`)}>
                Apri anagrafica
              </Button>
            </Card>
          )}
          <Card className="p-6">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-3">Info</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Creata da</span><span>{o.created_by_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Data</span><span className="font-mono">{o.created_at?.slice(0, 10)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Validità</span><span>{o.validita_giorni} giorni</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Versione</span><span>v{o.versione}</span></div>
            </div>
          </Card>
        </div>
      </div>

      {/* Send email dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invia offerta al cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>A</Label>
              <Input value={emailData.to} onChange={(e) => setEmailData({ ...emailData, to: e.target.value })} data-testid="email-to" />
            </div>
            <div>
              <Label>Oggetto</Label>
              <Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })} data-testid="email-subject" />
            </div>
            <div>
              <Label>Testo</Label>
              <Textarea rows={6} value={emailData.body} onChange={(e) => setEmailData({ ...emailData, body: e.target.value })} data-testid="email-body" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={emailData.cc_interno} onChange={(e) => setEmailData({ ...emailData, cc_interno: e.target.checked })} />
              Invia in copia anche a me
            </label>
            <div className="text-xs bg-slate-50 border rounded p-3 text-slate-600 flex items-center gap-2">
              <FileText className="w-4 h-4" /> In allegato: <strong>{o.numero}.pdf</strong>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button onClick={send} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="confirm-send-btn">
              <Send className="w-4 h-4 mr-1" /> Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rimanda al mittente</DialogTitle></DialogHeader>
          <div>
            <Label>Commento (motivo)</Label>
            <Textarea rows={4} value={rejectMsg} onChange={(e) => setRejectMsg(e.target.value)} className="mt-1.5" data-testid="reject-msg" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Annulla</Button>
            <Button onClick={reject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-btn">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract email dialog */}
      <Dialog open={contractEmailOpen} onOpenChange={setContractEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0F2C59] font-display flex items-center gap-2">
              <FileSignature className="w-5 h-5" /> Invia contratto al cliente
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Il contratto verrà generato dal template ufficiale e inviato come allegato PDF.
            Il cliente potrà firmarlo digitalmente o stamparlo, firmarlo e rispedirlo.
          </p>
          <div className="space-y-3">
            <div>
              <Label>Destinatario *</Label>
              <Input
                type="email"
                value={contractEmailForm.to}
                onChange={(e) => setContractEmailForm({ ...contractEmailForm, to: e.target.value })}
                className="mt-1.5"
                placeholder="cliente@example.com"
                data-testid="contract-email-to"
              />
            </div>
            <div>
              <Label>Oggetto</Label>
              <Input
                value={contractEmailForm.subject}
                onChange={(e) => setContractEmailForm({ ...contractEmailForm, subject: e.target.value })}
                className="mt-1.5"
                data-testid="contract-email-subject"
              />
            </div>
            <div>
              <Label>Messaggio aggiuntivo (opzionale)</Label>
              <Textarea
                rows={4}
                value={contractEmailForm.message}
                onChange={(e) => setContractEmailForm({ ...contractEmailForm, message: e.target.value })}
                className="mt-1.5"
                placeholder="Testo personalizzato che verrà aggiunto al messaggio standard…"
                data-testid="contract-email-message"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                checked={contractEmailForm.cc_interno}
                onChange={(e) => setContractEmailForm({ ...contractEmailForm, cc_interno: e.target.checked })}
                className="mt-0.5"
                data-testid="contract-email-cc"
              />
              <span>Invia in copia (CC) all'indirizzo interno commerciale</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractEmailOpen(false)} disabled={sendingContract}>Annulla</Button>
            <Button
              onClick={sendContract}
              disabled={sendingContract || !contractEmailForm.to?.trim()}
              className="bg-[#0F2C59] hover:bg-[#173F7A]"
              data-testid="contract-email-send"
            >
              {sendingContract ? "Invio in corso…" : "Invia contratto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0F2C59] font-display flex items-center gap-2"><Link2 className="w-5 h-5" /> Link pubblico offerta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Condividi questo link con il cliente per consentire la visualizzazione e l'accettazione online dell'offerta.</p>
            <div className="flex gap-2">
              <Input value={publicUrl || ""} readOnly className="font-mono text-xs" data-testid="public-link-input" />
              <Button variant="outline" onClick={copyLink} data-testid="copy-link-btn">Copia</Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => window.open(publicUrl, "_blank")} data-testid="open-public-btn">
                <ExternalLink className="w-4 h-4 mr-1" /> Apri anteprima
              </Button>
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
              💡 Il link è protetto da token sicuro e resta valido finché l'offerta non viene accettata o rifiutata dal cliente.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setLinkOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview modal */}
      <PdfPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        filename={`${o.numero}.pdf`}
        title={`Offerta ${o.numero}`}
        fetchBlob={async () => {
          const r = await api.get(`/offers/${id}/pdf`, { responseType: "blob" });
          return new Blob([r.data], { type: "application/pdf" });
        }}
      />
    </div>
  );
}
